import React from 'react';
import { Button, Tooltip, WizardFooterWrapper, useWizardContext } from '@patternfly/react-core';
import { TrashIcon } from '@patternfly/react-icons';

export interface DeleteResourcesReviewFooterProps {
  /** How many items the run would delete — the button says so out loud. */
  deleteCount: number;
  /** Whether the confirmation gate is satisfied (name typed, at least one action selected). */
  canConfirm: boolean;
  /** Why the danger button is disabled, shown on hover/focus. */
  disabledReason: string;
  submitting: boolean;
  /** Absent until the deletion is wired up: the button then stays disabled with the reason above. */
  onConfirm?: () => void;
}

/**
 * The review step's footer: Back, the danger action, Cancel.
 *
 * The danger button is `isAriaDisabled` rather than `isDisabled` whenever it cannot be used, so it
 * stays focusable and its tooltip is reachable (a hard-disabled button never shows one). PatternFly
 * blocks the click itself, so no extra guard is needed. Precedent: `RecapFooter` in the domain
 * registration wizard.
 */
const DeleteResourcesReviewFooter: React.FunctionComponent<DeleteResourcesReviewFooterProps> = ({
  deleteCount,
  canConfirm,
  disabledReason,
  submitting,
  onConfirm,
}) => {
  const { goToPrevStep, close } = useWizardContext();
  const enabled = canConfirm && onConfirm !== undefined && !submitting;
  const label = deleteCount === 0 ? 'Delete selected resources' : `Delete ${deleteCount} selected`;

  const dangerButton = (
    <Button
      variant="danger"
      icon={<TrashIcon />}
      isAriaDisabled={!enabled}
      isLoading={submitting}
      onClick={() => onConfirm?.()}
    >
      {label}
    </Button>
  );

  return (
    <WizardFooterWrapper>
      <Button variant="secondary" onClick={() => goToPrevStep()} isDisabled={submitting}>
        Back
      </Button>
      {enabled ? dangerButton : <Tooltip content={disabledReason}>{dangerButton}</Tooltip>}
      <Button variant="link" onClick={() => close()} isDisabled={submitting}>
        Cancel
      </Button>
    </WizardFooterWrapper>
  );
};

export default DeleteResourcesReviewFooter;
